import BaseStorageHandler from "./BaseStorageHandler";
import BaseModel from "../models/BaseModel";
import JSZip, { JSZipObject } from "jszip";
import BaseModelMap from "../models/BaseModelMap";
import Logger from "../Logger";
import VersionManagement from "./VersionManagement";
import { decodeFromCP437, encodeToCP437 } from "../utils/UmlautEncodeDecode";
import { updateToastProgress } from "../components/Toasts";
import ProjectMetadata from "../models/ProjectMetadata";

const logger = new Logger();
export default class ZipStorageHandler implements BaseStorageHandler {
  get isLocalStorgeHandle() {
    return false;
  }

  zip: JSZip = new JSZip();
  itemsFolder: JSZip | null = null;

  isOpen = false;

  items = new Map<string, any>();

  numberOfItemsToLoad = 0;
  numberOfItemsLoaded = 0;
  lastPercentage = 0;

  async ensureDbOpen(): Promise<boolean> {
    if (this.isOpen) {
      return true;
    }

    this.zip = new JSZip();
    this.itemsFolder = this.zip.folder("items"); // no itx
    this.isOpen = true;

    return true;
  }

  ///////////////////////////////////////////////////////////////////////////////////
  // Project Items
  ///////////////////////////////////////////////////////////////////////////////////

  async deleteProjectItems(projectId: string): Promise<boolean> {
    if (await this.ensureDbOpen()) {
      return new Promise<any>((resolve, reject) => {
        this.zip?.remove("items"); // no itx
        const emptyItems = this.zip?.folder("items"); // no itx
      });
    }
    return false;
  }

  saveData = async (item: BaseModel): Promise<boolean> => {
    if (await this.ensureDbOpen()) {
      const sJson = JSON.stringify(item);
      if (this.itemsFolder) {
        this.itemsFolder.file(item.id, sJson);
      }
      return true;
    } else {
      return false;
    }
  };

  //
  // for saving item into a zip file for writeing to clipboard
  //
  async saveItemsForClipboard(items: BaseModelMap<BaseModel>): Promise<boolean> {
    // state.validateImportExport();
    await items.saveToEstadaStorage(undefined, this.saveData, items);

    return true;
  }

  async saveDeletedIds(): Promise<boolean> {
    try {
      const sJson = JSON.stringify(state.objectRegistry.deletedIds);
      if (this.itemsFolder) {
        this.itemsFolder.file("deletedIds", sJson); // no itx
      }
      return true;
    } catch (ex) {
      logger.error(ex);
    }
    return false;
  }

  async saveProjectItems(projectId: string, showErrors = true): Promise<boolean> {
    try {
      const res = await state.saveToEstadaStorage(this.saveData, showErrors);
      return res;
    } catch (ex) {
      logger.error("Error in saveProjectItems", ex);
      return false;
    }
  }

  //
  // Higher Level: Only for localStrorage & SeverdataBasehandler
  //

  saveProjectItems2(projectId: string, blob: Blob): Promise<boolean> {
    throw new Error("Method not implemented.");
  }

  upateProgress(count: number): void {
    const basePercentage = 70;
    this.numberOfItemsLoaded += count;

    const percentage = basePercentage + (this.numberOfItemsLoaded / this.numberOfItemsToLoad) * 30;
    if (percentage - this.lastPercentage < 2) {
      return; // only update every 2 percent
    }
    this.lastPercentage = percentage;

    updateToastProgress({
      progress: percentage,
    });
  }

  async loadData(id: string | undefined, parentId: string | undefined): Promise<any> {
    if (await this.ensureDbOpen()) {
      if (id) {
        const item = this.items.get(id);
        if (item) {
          VersionManagement.updateItemFromOldVersion(state.projectMetadata.version, item);
        } else {
          logger.error("item==null", id);
        }

        this.upateProgress(1);

        return item;
      } else if (parentId) {
        const res: any = [];
        this.items.forEach((item) => {
          if (item?.parentId == parentId) {
            VersionManagement.updateItemFromOldVersion(state.projectMetadata.version, item);
            res.push(item);
          }
        });
        this.upateProgress(res.length);

        return res;
      }
    } else {
      return undefined;
    }
  }

  async loadDeletedIds(): Promise<boolean> {
    const deletedIds = this.items.get("deletedIds"); // no itx
    if (deletedIds) {
      state.objectRegistry.deletedIds = deletedIds;
    }

    return true;
  }

  async loadProjectItems(projectId: string): Promise<Blob | undefined> {
    console.error("loadProjectItems not implemented");
    return undefined;
  }

  async loadProjectItemsBlob(_projectId: string): Promise<Blob | undefined> {
    return undefined;
  }

  async loadProjectItemsFromZip(projectId: string): Promise<boolean> {
    logger.log("loadProjectFromDB");
    if (await this.ensureDbOpen()) {
      return await state.loadFromEstadaStorage(
        projectId,
        async (id: string | undefined, parentId: string | undefined): Promise<any> => {
          return await this.loadData(id, parentId);
        },
      );
    } else {
      return false;
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////
  // Metadata
  ///////////////////////////////////////////////////////////////////////////////////

  async deleteProjectMetadata(projectId: string): Promise<boolean> {
    return true;
  }

  async loadProjectMetadata(projectId: string): Promise<any> {
    if (await this.ensureDbOpen()) {
      const fMetadata = this.zip.file("metadata"); // no itx
      if (fMetadata) {
        const sJson = await fMetadata.async("string");
        return JSON.parse(sJson);
      }
    } else {
      return false;
    }
  }

  async saveProjectMetadata(projectId: string, data: ProjectMetadata): Promise<boolean> {
    if (await this.ensureDbOpen()) {
      const sJson = JSON.stringify(data);
      this.zip.file("metadata", sJson);
      return true;
    } else {
      return false;
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////
  // Projectlist
  ///////////////////////////////////////////////////////////////////////////////////

  async loadProjectList(): Promise<any> {
    return undefined;
  }

  ////////////////////////////////////////////////////////////////////////////////////
  // User
  ///////////////////////////////////////////////////////////////////////////////////

  async userIsLoggedIn(): Promise<boolean> {
    return true;
  }

  async login(params: { email: string; password: string }): Promise<any | undefined> {
    return params;
  }

  async getBlob(): Promise<Blob> {
    const blob = await this.zip.generateAsync({
      type: "blob",
      compression: "DEFLATE", // no itx
      compressionOptions: {
        level: 9,
      },
      encodeFileName: (data) => encodeToCP437(data),
    });
    logger.log(`getBlob blobsize=${blob.size}`);
    return blob;
  }

  async setBlob(blob: Blob): Promise<boolean> {
    try {
      logger.log(`setBlob blobsize=${blob?.size}`);

      await this.zip.loadAsync(blob, {
        decodeFileName: (data) => decodeFromCP437(data),
      });
      this.itemsFolder = this.zip.folder("items"); // no itx
      this.isOpen = true;

      const items = new Map<string, any>();
      this.items = items;

      const files = new Array<JSZipObject>();
      this.itemsFolder?.forEach(function (relativePath, file) {
        files.push(file);
      });

      this.numberOfItemsToLoad = files.length;
      this.numberOfItemsLoaded = 0;
      this.lastPercentage = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const sJson = await file.async("string");
        const obj = JSON.parse(sJson);
        const ss = file.name.split("/"); // to enable loading of "deletedIds"
        items.set(obj.id || ss[1], obj);
      }
      return true;
    } catch (ex) {
      logger.error(ex);
      return false;
    }
  }

  async loadPdfFile(id: string): Promise<Blob | undefined> {
    try {
    } catch (ex) {
      logger.error(ex);
    }
    return undefined;
  }

  async savePdfFile(id: string, blob: Blob): Promise<boolean> {
    try {
    } catch (ex) {
      logger.error(ex);
    }
    return false;
  }

  //
  // unused in zip
  //
  async saveInstallationChecks(projectId: string, ownerId: string, item: any): Promise<boolean> {
    return true;
  }

  async loadInstallationChecks(projectId: string, ownerId: string): Promise<any | undefined> {
    return undefined;
  }
}
